import React, { useState, useEffect } from 'react';
import { CalendarDays, Trash2, MapPin, Globe, Lock, ExternalLink, X, AlertTriangle, Edit, Save, Loader2 } from 'lucide-react';
import { Event, EventType, EVENT_TYPES } from '../types';
import { format, parseISO } from 'date-fns';
import { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface EventCardProps {
  event: Event;
  session: Session | null;
  onDelete?: () => void;
}

export function EventCard({ event, session, onDelete }: EventCardProps) {
  const [showMap, setShowMap] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedEvent, setEditedEvent] = useState<Event>(event);
  const [error, setError] = useState<string | null>(null);
  
  // Update editedEvent when the event prop changes
  useEffect(() => {
    setEditedEvent(event);
  }, [event]);
  
  const handleDelete = async () => {
    if (!session || isDeleting) return;
    setShowDeleteConfirm(true);
  };
  
  const confirmDelete = async () => {
    if (!session || isDeleting) return;
    
    try {
      setIsDeleting(true);
      
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', event.id);

      if (error) throw error;
      
      // Mark as deleted - this will hide the component until parent updates
      setIsDeleted(true);
      
      // Call the onDelete callback to refresh the parent component
      if (onDelete) {
        onDelete();
      }
      
    } catch (error) {
      console.error('Error deleting event:', error);
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!session || isSaving) return;
    
    try {
      setIsSaving(true);
      setError(null);
      
      // Validate required fields
      if (!editedEvent.title.trim() || !editedEvent.description.trim() || !editedEvent.date) {
        setError('Title, description, and date are required');
        setIsSaving(false);
        return;
      }
      
      const { error } = await supabase
        .from('events')
        .update({
          title: editedEvent.title,
          description: editedEvent.description,
          date: editedEvent.date,
          is_private: editedEvent.is_private,
          venue: editedEvent.venue || null,
          venue_lat: editedEvent.venue_lat || null,
          venue_lng: editedEvent.venue_lng || null,
          event_type: editedEvent.event_type || 'convention'
        })
        .eq('id', event.id);

      if (error) throw error;
      
      // Exit edit mode
      setIsEditing(false);
      
      // Call the onDelete callback to refresh the parent component
      if (onDelete) {
        onDelete();
      }
      
    } catch (error) {
      console.error('Error updating event:', error);
      setError('Failed to update event. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedEvent(event);
    setError(null);
  };

  const hasLocation = event.venue && event.venue_lat && event.venue_lng;
  
  const openGoogleMaps = () => {
    if (event.venue_lat && event.venue_lng) {
      const url = `https://www.google.com/maps?q=${event.venue_lat},${event.venue_lng}`;
      window.open(url, '_blank');
    }
  };

  // Get event type color
  const getEventTypeColor = (type: EventType): string => {
    switch (type) {
      case 'convention': return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-300';
      case 'church': return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300';
      case 'home group': return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300';
      case 'prayer meeting': return 'bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-300';
      case 'business': return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
      case 'family': return 'bg-rose-100 dark:bg-rose-900 text-rose-800 dark:text-rose-300';
      case 'other': return 'bg-cyan-100 dark:bg-cyan-900 text-cyan-800 dark:text-cyan-300';
      default: return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-300';
    }
  };

  // If the event has been deleted, don't render anything
  if (isDeleted) {
    return null;
  }

  // Check if the current user is the creator of the event
  const isCreator = session?.user?.id === event.user_id;

  return (
    <div className="bg-green-50 dark:bg-green-900/50 rounded-lg shadow-md p-4 mb-4 relative">
      {showDeleteConfirm && (
        <div className="absolute inset-0 bg-white dark:bg-gray-800 rounded-lg z-10 flex flex-col items-center justify-center p-4">
          <AlertTriangle size={32} className="text-amber-500 mb-2" />
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">Delete Event?</h3>
          <p className="text-gray-600 dark:text-gray-400 text-center mb-4">
            Are you sure you want to delete "{event.title}"? This action cannot be undone.
          </p>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={confirmDelete}
              disabled={isDeleting}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center"
            >
              {isDeleting ? (
                <>
                  <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 size={16} className="mr-2" />
                  Delete
                </>
              )}
            </button>
          </div>
        </div>
      )}
      
      <div className="flex justify-between items-start">
        <div className="w-full">
          {isEditing ? (
            <div className="space-y-4">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title
                </label>
                <input
                  id="title"
                  type="text"
                  value={editedEvent.title}
                  onChange={(e) => setEditedEvent({...editedEvent, title: e.target.value})}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  disabled={isSaving}
                />
              </div>
              
              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date
                </label>
                <input
                  id="date"
                  type="date"
                  value={editedEvent.date.split('T')[0]}
                  onChange={(e) => setEditedEvent({...editedEvent, date: e.target.value})}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  disabled={isSaving}
                />
              </div>
              
              <div>
                <label htmlFor="event-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Event Type
                </label>
                <select
                  id="event-type"
                  value={editedEvent.event_type || 'convention'}
                  onChange={(e) => setEditedEvent({...editedEvent, event_type: e.target.value as EventType})}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  disabled={isSaving}
                >
                  {EVENT_TYPES.map(type => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description (Markdown supported)
                </label>
                <textarea
                  id="description"
                  value={editedEvent.description}
                  onChange={(e) => setEditedEvent({...editedEvent, description: e.target.value})}
                  rows={5}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono"
                  disabled={isSaving}
                  placeholder="You can use Markdown formatting:
**bold text** or __bold text__
*italic text* or _italic text_
# Heading 1
## Heading 2
- Bullet point
1. Numbered list
[Link text](https://example.com)
> Blockquote"
                />
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-md">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Preview:</h4>
                <div className="p-3 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                  <ReactMarkdown 
                    className="text-gray-700 dark:text-gray-300 prose dark:prose-invert max-w-none"
                    remarkPlugins={[remarkGfm]}
                  >
                    {editedEvent.description}
                  </ReactMarkdown>
                </div>
              </div>
              
              <div>
                <label htmlFor="venue" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Venue
                </label>
                <input
                  id="venue"
                  type="text"
                  value={editedEvent.venue || ''}
                  onChange={(e) => setEditedEvent({...editedEvent, venue: e.target.value})}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  disabled={isSaving}
                />
              </div>
              
              <div className="flex items-center">
                <input
                  id="is-private"
                  type="checkbox"
                  checked={editedEvent.is_private}
                  onChange={(e) => setEditedEvent({...editedEvent, is_private: e.target.checked})}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  disabled={isSaving}
                />
                <label htmlFor="is-private" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                  {editedEvent.is_private ? (
                    <span className="flex items-center">
                      <Lock size={16} className="mr-1 text-amber-600 dark:text-amber-400" />
                      Private (only visible to you)
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <Globe size={16} className="mr-1 text-green-600 dark:text-green-400" />
                      Public (visible to everyone)
                    </span>
                  )}
                </label>
              </div>
              
              {error && (
                <div className="bg-red-50 dark:bg-red-900/50 text-red-600 dark:text-red-300 p-3 rounded-md text-sm">
                  {error}
                </div>
              )}
              
              <div className="flex space-x-3 justify-end">
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
                >
                  {isSaving ? (
                    <>
                      <Loader2 size={16} className="animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} className="mr-2" />
                      Save
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center space-x-2 mb-2">
                <CalendarDays size={18} className="text-green-600 dark:text-green-400" />
                <h3 className="text-lg font-semibold text-green-800 dark:text-green-300">
                  {event.title}
                </h3>
                {event.is_private ? (
                  <Lock size={16} className="text-amber-600 dark:text-amber-400" title="Private event" />
                ) : (
                  <Globe size={16} className="text-blue-600 dark:text-blue-400" title="Public event" />
                )}
              </div>
              
              <div className="flex flex-wrap gap-2 mb-2">
                <span className="text-sm px-2 py-0.5 rounded-full text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700">
                  {format(parseISO(event.date), 'MMMM d, yyyy')}
                </span>
                
                <span className={`text-sm px-2 py-0.5 rounded-full ${getEventTypeColor(event.event_type || 'convention')}`}>
                  {(event.event_type || 'convention').charAt(0).toUpperCase() + (event.event_type || 'convention').slice(1)}
                </span>
              </div>
              
              {event.venue && (
                <div className="flex items-start space-x-2 mb-2">
                  <MapPin size={16} className="text-green-600 dark:text-green-400 mt-1 flex-shrink-0" />
                  <div>
                    {hasLocation ? (
                      <button 
                        onClick={() => setShowMap(!showMap)}
                        className="text-green-700 dark:text-green-400 text-sm text-left hover:underline cursor-pointer"
                      >
                        {event.venue}
                      </button>
                    ) : (
                      <span className="text-green-700 dark:text-green-400 text-sm">
                        {event.venue}
                      </span>
                    )}
                    {hasLocation && (
                      <button
                        onClick={openGoogleMaps}
                        className="text-blue-600 dark:text-blue-400 text-xs flex items-center mt-1 hover:underline"
                      >
                        View on Google Maps
                        <ExternalLink size={12} className="ml-1" />
                      </button>
                    )}
                  </div>
                </div>
              )}
              
              <div className="text-green-700 dark:text-green-400 prose dark:prose-invert max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {event.description}
                </ReactMarkdown>
              </div>
              
              {hasLocation && showMap && (
                <div className="mt-3">
                  <iframe
                    title={`Map for ${event.title}`}
                    width="100%"
                    height="200"
                    style={{ border: 0, borderRadius: '0.375rem' }}
                    loading="lazy"
                    allowFullScreen
                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyDmA42Hubdx9PiZosahjp_8kRd9Y7Kxc14&q=${event.venue_lat},${event.venue_lng}&zoom=15`}
                  ></iframe>
                </div>
              )}
              
              {hasLocation && (
                <button
                  onClick={() => setShowMap(!showMap)}
                  className="text-blue-600 dark:text-blue-400 text-sm mt-2 hover:underline"
                >
                  {showMap ? 'Hide map' : 'Show map'}
                </button>
              )}
            </>
          )}
        </div>
        
        {isCreator && !isEditing && !isDeleting && !showDeleteConfirm && (
          <div className="flex space-x-2 flex-shrink-0 ml-2">
            <button
              onClick={handleEdit}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 p-1"
              aria-label="Edit event"
            >
              <Edit size={16} />
            </button>
            <button
              onClick={handleDelete}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 p-1"
              aria-label="Delete event"
            >
              <Trash2 size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}